import React, { useMemo, useRef, useState } from 'react';
import { Button } from '@appkit4/react-components/button';
import { Select } from '@appkit4/react-components/select';
import { FileModel, Upload } from '@appkit4/react-components/upload';
import Modal from '../../components/reusables/Modal';
import {
    useCommitRuleImport,
    useUploadImportFile,
} from '../../api/generated/endpoint';
import {
    RuleImportMode,
    type ImportUploadResponse,
} from '../../api/generated/models';
import { getImportUploadErrorMessage } from '../../api/imports';
import './RuleImportModal.scss';

// Mirrors backend RuleTypeId enum.
const RULE_TYPE_PROJECT_DATA = '6';
const RULE_TYPE_FIXED_ASSET = '7';
const ALL_PROJECTS = '0';
const MAX_FILE_SIZE_BYTES = 100 * 1024 * 1024;

export interface RuleImportProjectOption {
    projectId: number;
    label: string;
    hasData: boolean;
}

interface RuleImportModalProps {
    entityId: number;
    timeId: number;
    hasFixedAssetData: boolean;
    hasProjectData: boolean;
    projects: RuleImportProjectOption[];
    onClose: () => void;
    onImported: (rulesAdded: number) => void;
}

/** Appkit's Select returns either the value or the selected option. */
const selectedValue = (value: unknown): string =>
    typeof value === 'string'
        ? value
        : String((value as { value?: string } | undefined)?.value ?? '');

/**
 * FASORT's rule import: choose the rule type, project and template layout, then upload the file.
 * The parent mounts it only while open, so every open starts from a clean form.
 */
const RuleImportModal: React.FC<RuleImportModalProps> = ({
    entityId,
    timeId,
    hasFixedAssetData,
    hasProjectData,
    projects,
    onClose,
    onImported,
}) => {
    // FASORT defaulted to Fixed Asset; fall back to Project Data when only that has been imported.
    const [ruleTypeId, setRuleTypeId] = useState<string>(
        hasFixedAssetData ? RULE_TYPE_FIXED_ASSET : RULE_TYPE_PROJECT_DATA
    );
    const [projectId, setProjectId] = useState<string>(ALL_PROJECTS);
    const [mode, setMode] = useState<string>(String(RuleImportMode.NUMBER_1));
    const [upload, setUpload] = useState<ImportUploadResponse>();
    const [worksheetIndex, setWorksheetIndex] = useState<string>('0');
    const [error, setError] = useState<string>();
    const [isUploading, setIsUploading] = useState<boolean>(false);
    const [isImporting, setIsImporting] = useState<boolean>(false);
    const uploadRequestId = useRef(0);

    const uploadImport = useUploadImportFile();
    const commitImport = useCommitRuleImport();

    const ruleTypeOptions = useMemo(
        () => [
            {
                value: RULE_TYPE_FIXED_ASSET,
                label: 'Fixed Asset',
                disabled: !hasFixedAssetData,
            },
            {
                value: RULE_TYPE_PROJECT_DATA,
                label: 'Default Project',
                disabled: !hasProjectData,
            },
        ],
        [hasFixedAssetData, hasProjectData]
    );

    // Rules resolve against a project's data, so projects without any are dimmed.
    const projectOptions = useMemo(
        () => [
            { value: ALL_PROJECTS, label: 'All Projects', disabled: false },
            ...projects.map((project) => ({
                value: String(project.projectId),
                label: project.label,
                disabled: !project.hasData,
            })),
        ],
        [projects]
    );

    const modeOptions = [
        { value: String(RuleImportMode.NUMBER_1), label: 'Default' },
        { value: String(RuleImportMode.NUMBER_0), label: 'Legacy' },
    ];

    const worksheetOptions = useMemo(
        () =>
            (upload?.worksheets ?? []).map((sheet, index) => ({
                value: String(sheet.index ?? index),
                label: sheet.name ?? `Sheet ${index + 1}`,
            })),
        [upload]
    );

    const canImport = !!upload && !isUploading && !isImporting;

    const handleUploadChange = (uploadFile: FileModel): void => {
        const file = uploadFile.originFile;
        const requestId = ++uploadRequestId.current;

        setUpload(undefined);
        setError(undefined);

        if (!(file instanceof File)) {
            setError('The selected file could not be read.');
            return;
        }

        setIsUploading(true);
        uploadImport
            .mutateAsync({
                data: { EntityId: entityId, TimeId: timeId, File: file },
            })
            .then((response) => {
                if (requestId !== uploadRequestId.current) return;
                setUpload(response);
                setWorksheetIndex(String(response.worksheets?.[0]?.index ?? 0));
            })
            .catch((uploadError: unknown) => {
                if (requestId !== uploadRequestId.current) return;
                setError(getImportUploadErrorMessage(uploadError));
            })
            .finally(() => {
                if (requestId === uploadRequestId.current) {
                    setIsUploading(false);
                }
            });
    };

    const handleImport = (): void => {
        if (!upload?.uploadId) return;

        setError(undefined);
        setIsImporting(true);
        commitImport
            .mutateAsync({
                data: {
                    uploadId: upload.uploadId,
                    entityId,
                    timeId,
                    ruleTypeId: Number(ruleTypeId),
                    projectId:
                        ruleTypeId === RULE_TYPE_PROJECT_DATA
                            ? Number(projectId)
                            : 0,
                    mode: Number(mode) as RuleImportMode,
                    worksheetIndex: Number(worksheetIndex),
                },
            })
            .then((response) => onImported(response.rulesAdded ?? 0))
            .catch((importError: unknown) =>
                setError(getImportUploadErrorMessage(importError))
            )
            .finally(() => setIsImporting(false));
    };

    return (
        <Modal
            visible
            title="Rule import"
            onClose={onClose}
            modalStyle={{ width: '600px' }}
            initialFocus={false}
        >
            <div className="rule-import-modal" data-testid="rule-import-modal">
                {error && (
                    <p
                        className="rule-import-modal__error"
                        data-testid="rule-import-error"
                    >
                        {error}
                    </p>
                )}

                <div className="split-rule-row split-rule-row--two-cols">
                    <div
                        className="split-rule-field"
                        data-testid="rule-import-type"
                    >
                        <Select
                            data={ruleTypeOptions}
                            value={ruleTypeId}
                            placeholder="Rule Type"
                            onSelect={(value: unknown) => {
                                setRuleTypeId(selectedValue(value));
                                setProjectId(ALL_PROJECTS);
                            }}
                        />
                    </div>
                    <div
                        className="split-rule-field"
                        data-testid="rule-import-mode"
                    >
                        <Select
                            data={modeOptions}
                            value={mode}
                            placeholder="Rule Template"
                            onSelect={(value: unknown) =>
                                setMode(selectedValue(value))
                            }
                        />
                    </div>
                </div>

                {ruleTypeId === RULE_TYPE_PROJECT_DATA && (
                    <div
                        className="split-rule-field"
                        data-testid="rule-import-project"
                    >
                        <Select
                            data={projectOptions}
                            value={projectId}
                            placeholder="Project"
                            onSelect={(value: unknown) =>
                                setProjectId(selectedValue(value))
                            }
                        />
                    </div>
                )}

                <div data-testid="rule-import-file">
                    <Upload
                        onChange={handleUploadChange}
                        // Removing the file must not leave its upload ready to import.
                        onRemove={() => {
                            uploadRequestId.current++;
                            setUpload(undefined);
                            setIsUploading(false);
                            setError(undefined);
                        }}
                        multiple={false}
                        autoUpload={false}
                        acceptFileType=".XLS,.XLSX"
                        uploadTitle="Upload your file"
                        uploadInstruction="You can upload Excel files (.xls, .xlsx). The max file size is 100 MB."
                        maxFileSize={MAX_FILE_SIZE_BYTES}
                        config={{
                            trigger: false,
                            type: 'inline',
                            size: true,
                        }}
                    />
                </div>

                {isUploading && (
                    <p className="rule-import-modal__hint">Uploading file…</p>
                )}

                {worksheetOptions.length > 1 && (
                    <div
                        className="split-rule-field"
                        data-testid="rule-import-worksheet"
                    >
                        <Select
                            data={worksheetOptions}
                            value={worksheetIndex}
                            placeholder="Worksheet"
                            onSelect={(value: unknown) =>
                                setWorksheetIndex(selectedValue(value))
                            }
                        />
                    </div>
                )}

                <div className="deleteButtons">
                    <Button kind="secondary" onClick={onClose}>
                        Cancel
                    </Button>
                    <Button
                        kind="primary"
                        disabled={!canImport}
                        loading={isImporting}
                        onClick={handleImport}
                        data-testid="rule-import-submit"
                    >
                        Import
                    </Button>
                </div>
            </div>
        </Modal>
    );
};

export default RuleImportModal;
